import React, { useState } from "react";
import { Wand2, Trash2, Copy, Check } from "lucide-react";
import { useToast } from "../hooks/use-toast";

export default function PaleCombinator() {
  const [input, setInput] = useState("12, 34, 56, 78");
  const [pales, setPales] = useState([]);
  const [copied, setCopied] = useState(false);
  const { toast } = useToast();

  const generate = () => {
    const nums = input
      .split(/[,\s]+/)
      .map((s) => s.trim())
      .filter(Boolean);
    if (nums.length < 2) {
      toast({ title: "Ingresa al menos 2 números", description: "Sepáralos con comas. Ejemplo: 12, 34, 56" });
      return;
    }
    const result = [];
    for (let i = 0; i < nums.length; i++) {
      for (let j = i + 1; j < nums.length; j++) {
        result.push(`${nums[i]}-${nums[j]}`);
      }
    }
    setPales(result);
    toast({ title: "Palés generados", description: `${result.length} combinaciones creadas.` });
  };

  const clear = () => {
    setInput("");
    setPales([]);
  };

  const copyAll = async () => {
    if (!pales.length) return;
    try {
      await navigator.clipboard.writeText(pales.join(", "));
      setCopied(true);
      toast({ title: "Copiado correctamente", description: "Los palés están en tu portapapeles." });
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast({ title: "Error al copiar" });
    }
  };

  return (
    <section className="py-16 px-4 bg-gradient-to-b from-white to-blue-50/40">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-10">
          <h2 className="np-title inline-block text-2xl sm:text-3xl font-extrabold text-[#0e4a7a]">
            Combinador de Palés
          </h2>
          <p className="text-gray-600 mt-4 text-sm sm:text-base">
            Genera automáticamente todas las combinaciones posibles con tus números.
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-xl border border-blue-100 p-6 sm:p-8">
          <label className="text-sm font-semibold text-[#0e4a7a]">
            Escribe tus números separados por comas
          </label>
          <p className="text-xs text-gray-500 mb-3">Ejemplo: 12, 34, 56, 78</p>

          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="12, 34, 56, 78"
            className="w-full bg-blue-50/60 border-2 border-blue-100 focus:border-[#1d6cb1] rounded-xl px-4 py-3 text-lg font-medium tracking-wide text-[#0e4a7a] outline-none transition"
          />

          <div className="mt-4 flex flex-wrap gap-3">
            <button
              onClick={generate}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-gradient-to-r from-[#1d6cb1] to-[#2a83ce] text-white font-semibold shadow-md hover:shadow-lg hover:scale-[1.02] transition"
            >
              <Wand2 className="w-4 h-4" /> Generar Palés
            </button>
            <button
              onClick={clear}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-gray-100 text-gray-700 font-semibold hover:bg-gray-200 transition"
            >
              <Trash2 className="w-4 h-4" /> Limpiar
            </button>
            <button
              onClick={copyAll}
              disabled={!pales.length}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-emerald-500 text-white font-semibold hover:bg-emerald-600 disabled:opacity-50 disabled:cursor-not-allowed transition"
            >
              {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              {copied ? "Copiado" : "Copiar Palés"}
            </button>
          </div>

          <div className="mt-6 bg-blue-50/50 rounded-xl p-4 border border-blue-100">
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm font-semibold text-[#0e4a7a]">Palés generados</p>
              <span className="text-xs px-2.5 py-1 rounded-full bg-[#1d6cb1] text-white font-bold">
                Total: {pales.length}
              </span>
            </div>
            {pales.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {pales.map((p, i) => (
                  <span
                    key={i}
                    className="px-3 py-1.5 bg-white rounded-lg border border-blue-200 text-sm font-mono font-semibold text-[#0e4a7a] shadow-sm hover:bg-blue-50 transition"
                  >
                    {p}
                  </span>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500 italic">Aún no has generado palés. Escribe tus números y haz clic en “Generar Palés”.</p>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
